import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDialog } from './CalendarDialog';
import { updateMyReservation, clearError } from '../redux/slices/myselfSlice';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingModal } from './LoadingModal';
import { getEquipmentTranslation } from '../utils/equipmentTranslation';

const EditReservationModal = ({
  setShowEditReservationModal,
  reservation,
  equipment,
  renterName,
  onReservationUpdated,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.myself.loading);
  const myselfError = useSelector((state) => state.myself.error);
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const equipmentName = getEquipmentTranslation(equipment, 'name', currentLang);
  const [formData, setFormData] = useState({
    deliveryAssistance: '',
    pickupAssistance: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    additionalNotes: '',
    pickupLocation: '',
    dropOffLocation: '',
  });
  const [showTransportConfirmation, setShowTransportConfirmation] = useState(false);
  const [transportConfirmationChecked, setTransportConfirmationChecked] = useState(false);

  useEffect(() => {
    // Initialize form data with existing reservation data
    if (reservation) {
      setFormData({
        deliveryAssistance: reservation.deliveryAssistance ? 'yes' : 'no',
        pickupAssistance: reservation.pickupAssistance ? 'yes' : 'no',
        startDate: reservation.startDate
          ? new Date(reservation.startDate).toISOString().split('T')[0]
          : '',
        endDate: reservation.endDate
          ? new Date(reservation.endDate).toISOString().split('T')[0]
          : '',
        paymentMethod: reservation.paymentMethod || '',
        additionalNotes: reservation.additionalNotes || '',
        pickupLocation: reservation.pickupLocation || '',
        dropOffLocation: reservation.dropOffLocation || '',
      });
    }
    dispatch(clearError());
  }, [reservation, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleDateRangeChange = (range) => {
    if (!range || !range.from) {
      setFormData({
        ...formData,
        startDate: '',
        endDate: '',
      });
      return;
    }

    setFormData({
      ...formData,
      startDate: range.from,
      endDate: range.to || '',
    });
  };

  const handleCloseModal = () => {
    dispatch(clearError());
    setShowEditReservationModal(false);
  };

  const needsTransportConfirmation = () => {
    return formData.deliveryAssistance === 'no' || formData.pickupAssistance === 'no';
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (needsTransportConfirmation() && !showTransportConfirmation) {
      setShowTransportConfirmation(true);
    } else {
      handleUpdateReservation();
    }
  };

  const handleTransportConfirmationClose = () => {
    setShowTransportConfirmation(false);
    setTransportConfirmationChecked(false);
  };

  const handleUpdateReservation = async () => {
    dispatch(
      updateMyReservation({
        _id: reservation._id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        deliveryAssistance: formData.deliveryAssistance === 'yes',
        pickupAssistance: formData.pickupAssistance === 'yes',
        paymentMethod: formData.paymentMethod,
        additionalNotes: formData.additionalNotes,
        pickupLocation: formData.pickupLocation,
        dropOffLocation: formData.dropOffLocation,
      })
    ).then((result) => {
      if (!result.error) {
        setShowEditReservationModal(false);
        // Refresh reservations after successful update
        if (onReservationUpdated) {
          onReservationUpdated();
        }
      }
    });
  };

  const handleConfirmationCheckboxChange = (e) => {
    setTransportConfirmationChecked(e.target.checked);
  };

  if (!reservation || !equipment) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {(loading || myselfError) && (
        <LoadingModal
          message={t('reservations.editModal.updatingReservation')}
          error={myselfError}
          onClose={handleDismissError}
        />
      )}

      {/* Transport Confirmation Popup */}
      {showTransportConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg border-4 border-red-500 shadow-xl w-11/12 max-w-md p-6 outline outline-2 outline-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-red-600">
              {t('reservations.createModal.transportConfirmation.title')}
            </h3>
            <p className="mb-4 text-gray-700">
              {t('reservations.createModal.transportConfirmation.message')}
            </p>

            {equipment.selfTransport && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="font-semibold text-gray-800 mb-1">
                  {t('reservations.createModal.transportConfirmation.transportRequirements')}
                </h4>
                <p className="text-gray-700">{equipment.selfTransport}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={transportConfirmationChecked}
                  onChange={handleConfirmationCheckboxChange}
                  className="form-checkbox h-5 w-5 text-green-500"
                />
                <span className="text-gray-800">
                  {t('reservations.createModal.transportConfirmation.acceptResponsibility')}
                </span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleTransportConfirmationClose}
                type="button"
              >
                {t('reservations.cancel')}
              </button>
              <button
                className={`bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 ml-4 rounded-md transition duration-200 ${
                  !transportConfirmationChecked ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleUpdateReservation}
                type="button"
                disabled={!transportConfirmationChecked}
              >
                {t('reservations.createModal.transportConfirmation.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Edit Reservation Modal */}
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">
          {t('reservations.editModal.title')}
        </h2>

        {/* Reservation Info Display */}
        <div className="space-y-2 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">
              {t('reservations.createModal.renterName')}:
            </span>
            <span className="text-gray-900">{renterName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">
              {t('reservations.createModal.equipment')}:
            </span>
            <span className="text-gray-900">{equipmentName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">
              {t('reservations.createModal.location')}:
            </span>
            <span className="text-gray-900">{equipment.location}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">
              {t('reservations.createModal.price')}:
            </span>
            <span className="text-gray-900">${equipment.rentalPrice}/day</span>
          </div>
        </div>

        <form onSubmit={handleSubmitClick}>
          {/* Date Selection */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-2">
              {t('reservations.editModal.rentalDates')}
            </label>
            <div className="flex justify-center">
              {equipment && equipment._id && (
                <CalendarDialog
                  value={{
                    from: formData.startDate || null,
                    to: formData.endDate || null,
                  }}
                  onChange={handleDateRangeChange}
                  equipmentId={equipment._id}
                  quantity={reservation?.quantity || reservation?.rentalTotal || 1}
                  totalInstances={equipment.totalInstances || 0}
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-4 my-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{t('reservations.startDate')}:</span>
                <span className="text-gray-900 font-bold">
                  {formData.startDate ? new Date(formData.startDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{t('reservations.endDate')}:</span>
                <span className="text-gray-900 font-bold">
                  {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery Assistance */}
          <div className="flex justify-between items-center mb-4">
            <label className="font-medium text-gray-700 flex items-center">
              {t('reservations.createModal.deliveryAssistance')}
              <span
                title={t('reservations.createModal.deliveryTooltip')}
                className="ml-1 text-gray-500 cursor-help inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs"
              >
                ?
              </span>
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="deliveryAssistance"
                  value="yes"
                  checked={formData.deliveryAssistance === 'yes'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="text-gray-900">{t('reservations.createModal.yes')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="deliveryAssistance"
                  value="no"
                  checked={formData.deliveryAssistance === 'no'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="text-gray-900">{t('reservations.createModal.no')}</span>
              </label>
            </div>
          </div>

          {/* Pickup Assistance */}
          <div className="flex justify-between items-center mb-4">
            <label className="font-medium text-gray-700">
              {t('reservations.createModal.pickupAssistance')}
              <span
                title={t('reservations.createModal.pickupTooltip')}
                className="ml-1 text-gray-500 cursor-help inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs"
              >
                ?
              </span>
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="pickupAssistance"
                  value="yes"
                  checked={formData.pickupAssistance === 'yes'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="text-gray-900">{t('reservations.createModal.yes')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="pickupAssistance"
                  value="no"
                  checked={formData.pickupAssistance === 'no'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="text-gray-900">{t('reservations.createModal.no')}</span>
              </label>
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-2">
              {t('reservations.createModal.paymentMethod')}
            </label>
            <select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('reservations.createModal.selectPaymentMethod')}</option>
              <option value="credit_card">{t('reservations.createModal.creditCard')}</option>
              <option value="debit_card">{t('reservations.createModal.debitCard')}</option>
              <option value="bank_transfer">{t('reservations.createModal.bankTransfer')}</option>
              <option value="cash">{t('reservations.createModal.cash')}</option>
              <option value="check">{t('reservations.createModal.check')}</option>
            </select>
          </div>

          {/* Pickup Location */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-2">
              {t('reservations.createModal.pickupLocation')}
            </label>
            <input
              type="text"
              name="pickupLocation"
              value={formData.pickupLocation}
              onChange={handleInputChange}
              placeholder={t('reservations.createModal.pickupLocationPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Drop-off Location */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-2">
              {t('reservations.createModal.dropOffLocation')}
            </label>
            <input
              type="text"
              name="dropOffLocation"
              value={formData.dropOffLocation}
              onChange={handleInputChange}
              placeholder={t('reservations.createModal.dropOffLocationPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Additional Notes */}
          <div className="mb-6">
            <label className="block font-medium text-gray-700 mb-2">
              {t('reservations.createModal.additionalNotes')}
            </label>
            <textarea
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              placeholder={t('reservations.createModal.additionalNotesPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
              onClick={handleCloseModal}
              type="button"
            >
              {t('reservations.cancel')}
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-5 ml-4 rounded-md transition duration-200"
              type="submit"
            >
              {t('reservations.editModal.updateReservation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { EditReservationModal };
